import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserDetails, deleteUser } from "../../../redux/userRelated/userHandle";
import { useNavigate, useParams } from "react-router-dom";
import { getSubjectList } from "../../../redux/sclassRelated/sclassHandle";
import {
    alpha,
    Avatar,
    Box,
    Button,
    Chip,
    Collapse,
    Container,
    Fade,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import {
    KeyboardArrowDown,
    KeyboardArrowUp,
    Delete as DeleteIcon,
    ArrowBack,
    FactCheck,
    Grading,
    Person,
    School,
    BarChart as BarChartIcon,
    TableRows,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Contacts as GuardianIcon,
    Cake as CakeIcon,
    Event as EventIcon,
    Badge as BadgeIcon,
    AccountBalanceWallet as FeeIcon,
} from "@mui/icons-material";
import {
    calculateOverallAttendancePercentage,
    calculateSubjectAttendancePercentage,
    groupAttendanceBySubject,
} from "../../../components/attendanceCalculator";
import CustomBarChart from "../../../components/CustomBarChart";
import CustomPieChart from "../../../components/CustomPieChart";
import Popup from "../../../components/Popup";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";
import SpecialNeedsSection from "../../../components/student/SpecialNeedsSection";
import { resolveUploadedMediaUrl } from "../../../config/api";

const ViewStudent = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch();
    const { userDetails, response, loading, error } = useSelector((state) => state.user);

    const studentID = params.id;
    const address = "Student";

    const [tab, setTab] = useState("1");
    const [attendanceView, setAttendanceView] = useState("table");
    const [marksView, setMarksView] = useState("table");
    const [openStates, setOpenStates] = useState({});
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [deleteDialog, setDeleteDialog] = useState(false);

    useEffect(() => {
        dispatch(getUserDetails(studentID, address));
    }, [dispatch, studentID]);

    const classId = userDetails?.sclassName?._id ?? userDetails?.sclassName?.id;
    useEffect(() => {
        if (classId) {
            dispatch(getSubjectList(classId, "ClassSubjects"));
        }
    }, [dispatch, classId]);

    const [sclassName, setSclassName] = useState(null);
    const [studentSchool, setStudentSchool] = useState(null);
    const [subjectMarks, setSubjectMarks] = useState([]);
    const [subjectAttendance, setSubjectAttendance] = useState([]);

    useEffect(() => {
        if (userDetails) {
            setSclassName(userDetails.sclassName || null);
            setStudentSchool(userDetails.school || null);
            setSubjectMarks(Array.isArray(userDetails.examResult) ? userDetails.examResult : []);
            setSubjectAttendance(Array.isArray(userDetails.attendance) ? userDetails.attendance : []);
        }
    }, [userDetails]);

    const studentName = userDetails?.name || "this student";

    const handleConfirmDeleteStudent = async () => {
        const outcome = await dispatch(deleteUser(studentID, "Student"));
        setDeleteDialog(false);
        if (outcome?.success) {
            navigate("/Admin/students");
            return;
        }
        setMessage("Could not delete this student. You may need to remove related records first, or try again.");
        setShowPopup(true);
    };

    const handleOpen = (subId) => {
        setOpenStates((prev) => ({ ...prev, [subId]: !prev[subId] }));
    };

    const overallAttendancePercentage = calculateOverallAttendancePercentage(subjectAttendance);
    const overallAbsentPercentage = 100 - overallAttendancePercentage;

    const chartData = useMemo(
        () => [
            { name: "Present", value: overallAttendancePercentage },
            { name: "Absent", value: overallAbsentPercentage },
        ],
        [overallAttendancePercentage, overallAbsentPercentage]
    );

    const subjectData = useMemo(() => {
        return Object.entries(groupAttendanceBySubject(subjectAttendance)).map(
            ([subName, { subCode, present, sessions }]) => ({
                subject: subName,
                attendancePercentage: calculateSubjectAttendancePercentage(present, sessions),
                totalClasses: sessions,
                attendedClasses: present,
            })
        );
    }, [subjectAttendance]);

    const marksChartData = useMemo(() => {
        if (!Array.isArray(subjectMarks)) return [];
        return subjectMarks
            .filter((r) => r?.subName && (r.marksObtained !== undefined && r.marksObtained !== null))
            .map((r) => ({
                ...r,
                subject: typeof r.subName === "object" ? r.subName.subName : String(r.subName),
            }));
    }, [subjectMarks]);

    const classLabel = sclassName?.sclassName ?? "—";
    const schoolLabel = studentSchool?.schoolName ?? studentSchool ?? "—";
    const initial = (userDetails?.name || "?").charAt(0).toUpperCase();

    const formatUiDate = (value) => {
        if (value == null || value === "") return "—";
        if (typeof value === "object") return "—";
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
    };

    const feeDisplay = useMemo(() => {
        const f = userDetails?.feeBalance;
        if (f === undefined || f === null || f === "") return "—";
        const n = typeof f === "number" ? f : parseFloat(String(f), 10);
        if (Number.isNaN(n)) return "—";
        return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }, [userDetails?.feeBalance]);

    const photoSrc = useMemo(() => resolveUploadedMediaUrl(userDetails?.photoUrl), [userDetails?.photoUrl]);

    const statusColor = (() => {
        const s = (userDetails?.admissionStatus || "").toLowerCase();
        if (s === "active") return "success";
        if (s === "graduated") return "info";
        if (s === "suspended" || s === "withdrawn") return "error";
        if (s === "transferred") return "warning";
        return "default";
    })();

    const StudentDetailsSection = () => (
        <Stack spacing={3}>
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    background: (t) =>
                        `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.06)} 0%, ${alpha(t.palette.primary.light, 0.04)} 100%)`,
                }}
            >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems={{ sm: "center" }}>
                    <Avatar
                        src={photoSrc}
                        alt={userDetails?.name}
                        sx={{
                            width: 72,
                            height: 72,
                            fontSize: "1.75rem",
                            fontWeight: 700,
                            bgcolor: "primary.main",
                            boxShadow: (t) => `0 8px 24px ${alpha(t.palette.primary.main, 0.35)}`,
                        }}
                    >
                        {!photoSrc ? initial : null}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" fontWeight={800} gutterBottom>
                            {userDetails?.name}
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                            <Chip icon={<Person sx={{ "&&": { fontSize: 18 } }} />} label={`Roll ${userDetails?.rollNum ?? "—"}`} variant="outlined" />
                            <Chip icon={<School sx={{ "&&": { fontSize: 18 } }} />} label={classLabel} color="primary" variant="outlined" />
                            <Chip label={schoolLabel} variant="outlined" />
                            {userDetails?.gender ? <Chip label={userDetails.gender} variant="outlined" /> : null}
                            {userDetails?.admissionStatus ? (
                                <Chip icon={<BadgeIcon sx={{ "&&": { fontSize: 18 } }} />} label={userDetails.admissionStatus} color={statusColor} variant="outlined" />
                            ) : null}
                        </Stack>
                        {subjectAttendance?.length > 0 && (
                            <Box sx={{ maxWidth: 280, mt: 1 }}>
                                <CustomPieChart data={chartData} />
                            </Box>
                        )}
                    </Box>
                    <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteDialog(true)} sx={{ borderRadius: 2, alignSelf: { xs: "stretch", sm: "center" } }}>
                        Delete student
                    </Button>
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Student record
                </Typography>
                <Stack spacing={2.5}>
                    <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                        <Chip icon={<CakeIcon sx={{ "&&": { fontSize: 18 } }} />} label={`DOB: ${formatUiDate(userDetails?.dateOfBirth)}`} variant="outlined" sx={{ borderRadius: 2 }} />
                        <Chip icon={<EventIcon sx={{ "&&": { fontSize: 18 } }} />} label={`Admitted: ${formatUiDate(userDetails?.admissionDate)}`} variant="outlined" sx={{ borderRadius: 2 }} />
                        <Chip icon={<FeeIcon sx={{ "&&": { fontSize: 18 } }} />} label={`Fee balance: ${feeDisplay}`} variant="outlined" color="secondary" sx={{ borderRadius: 2 }} />
                    </Stack>
                    <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.6}>
                            GUARDIAN / PARENT
                        </Typography>
                        <Stack spacing={0.75} sx={{ mt: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <GuardianIcon fontSize="small" color="action" />
                                <Typography variant="body2">{userDetails?.guardianName?.trim() || "—"}</Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PhoneIcon fontSize="small" color="action" />
                                <Typography variant="body2">{userDetails?.guardianPhone?.trim() || "—"}</Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <EmailIcon fontSize="small" color="action" />
                                <Typography variant="body2">{userDetails?.guardianEmail?.trim() || "—"}</Typography>
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Quick actions
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap">
                    <Button variant="outlined" onClick={() => navigate(`/Admin/students/student/attendance/${studentID}`)} startIcon={<FactCheck />} sx={{ borderRadius: 2 }}>
                        View attendance
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={() => navigate(`/Admin/students/student/marks/${studentID}`)} startIcon={<Grading />} sx={{ borderRadius: 2 }}>
                        View marks
                    </Button>
                    <Button variant="contained" onClick={() => navigate(`/Admin/students/report-card/${studentID}`)} sx={{ borderRadius: 2 }}>
                        Report card
                    </Button>
                    <Button variant="text" onClick={() => navigate("/Admin/exams/review")} sx={{ borderRadius: 2 }}>
                        Review marks
                    </Button>
                    <Button variant="text" onClick={() => navigate("/Admin/attendance/review")} sx={{ borderRadius: 2 }}>
                        Review attendance
                    </Button>
                </Stack>
            </Paper>
        </Stack>
    );

    const StudentAttendanceSection = () => (
        <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={2}>
                <Typography variant="h6" fontWeight={700}>
                    Attendance
                </Typography>
                <ToggleButtonGroup
                    value={attendanceView}
                    exclusive
                    onChange={(_, v) => v && setAttendanceView(v)}
                    size="small"
                    sx={{ bgcolor: (t) => alpha(t.palette.action.hover, 0.5), borderRadius: 2, p: 0.5 }}
                >
                    <ToggleButton value="table" sx={{ borderRadius: 1.5, textTransform: "none", gap: 0.75 }}>
                        <TableRows fontSize="small" /> Table
                    </ToggleButton>
                    <ToggleButton value="chart" sx={{ borderRadius: 1.5, textTransform: "none", gap: 0.75 }}>
                        <BarChartIcon fontSize="small" /> Chart
                    </ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {subjectAttendance?.length > 0 ? (
                <>
                    {attendanceView === "table" && (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }}>
                                        <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Present</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Sessions</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>%</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            Details
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(groupAttendanceBySubject(subjectAttendance)).map(
                                        ([subName, { present, allData, subId, sessions }]) => {
                                            const pct = calculateSubjectAttendancePercentage(present, sessions);
                                            return (
                                                <React.Fragment key={subId}>
                                                    <TableRow hover>
                                                        <TableCell>{subName}</TableCell>
                                                        <TableCell>{present}</TableCell>
                                                        <TableCell>{sessions}</TableCell>
                                                        <TableCell>{pct}%</TableCell>
                                                        <TableCell align="right">
                                                            <Button size="small" variant="outlined" endIcon={openStates[subId] ? <KeyboardArrowUp /> : <KeyboardArrowDown />} onClick={() => handleOpen(subId)} sx={{ borderRadius: 2 }}>
                                                                Session log
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell colSpan={5} sx={{ py: 0, border: 0, bgcolor: (t) => alpha(t.palette.action.hover, 0.35) }}>
                                                            <Collapse in={openStates[subId]} timeout="auto" unmountOnExit>
                                                                <Box sx={{ py: 2, px: 1 }}>
                                                                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                                                        Session log
                                                                    </Typography>
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell>Date</TableCell>
                                                                                <TableCell align="right">Status</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {allData.map((data, idx) => {
                                                                                const date = new Date(data.date);
                                                                                const dateString =
                                                                                    date.toString() !== "Invalid Date" ? date.toISOString().substring(0, 10) : "—";
                                                                                return (
                                                                                    <TableRow key={idx}>
                                                                                        <TableCell>{dateString}</TableCell>
                                                                                        <TableCell align="right">{data.status}</TableCell>
                                                                                    </TableRow>
                                                                                );
                                                                            })}
                                                                        </TableBody>
                                                                    </Table>
                                                                </Box>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            );
                                        }
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {attendanceView === "chart" && (
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <CustomBarChart chartData={subjectData} dataKey="attendancePercentage" />
                        </Paper>
                    )}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                            Overall attendance: <strong>{overallAttendancePercentage.toFixed(1)}%</strong>
                        </Typography>
                        <Button variant="outlined" size="small" onClick={() => navigate(`/Admin/students/student/attendance/${studentID}`)} sx={{ borderRadius: 2 }}>
                            Full attendance log
                        </Button>
                    </Stack>
                </>
            ) : (
                <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
                    <Typography color="text.secondary" gutterBottom>
                        No attendance recorded yet.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Attendance is entered by teachers and approved under Attendance → Attendance review.
                    </Typography>
                </Paper>
            )}
        </Stack>
    );

    const StudentMarksSection = () => (
        <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={2}>
                <Typography variant="h6" fontWeight={700}>
                    Exam marks
                </Typography>
                <ToggleButtonGroup
                    value={marksView}
                    exclusive
                    onChange={(_, v) => v && setMarksView(v)}
                    size="small"
                    sx={{ bgcolor: (t) => alpha(t.palette.action.hover, 0.5), borderRadius: 2, p: 0.5 }}
                >
                    <ToggleButton value="table" sx={{ borderRadius: 1.5, textTransform: "none", gap: 0.75 }}>
                        <TableRows fontSize="small" /> Table
                    </ToggleButton>
                    <ToggleButton value="chart" sx={{ borderRadius: 1.5, textTransform: "none", gap: 0.75 }}>
                        <BarChartIcon fontSize="small" /> Chart
                    </ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {subjectMarks?.length > 0 ? (
                <>
                    {marksView === "table" && (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }}>
                                        <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Marks</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {subjectMarks.map((result, index) => {
                                        if (!result?.subName || result.marksObtained === undefined || result.marksObtained === null) {
                                            return null;
                                        }
                                        const label = typeof result.subName === "object" ? result.subName.subName : String(result.subName);
                                        return (
                                            <TableRow hover key={index}>
                                                <TableCell>{label}</TableCell>
                                                <TableCell>{result.marksObtained}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {marksView === "chart" && marksChartData.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, overflow: "auto" }}>
                            <CustomBarChart chartData={marksChartData} dataKey="marksObtained" />
                        </Paper>
                    )}
                    <Button variant="outlined" onClick={() => navigate(`/Admin/students/student/marks/${studentID}`)} sx={{ borderRadius: 2, alignSelf: "flex-start" }}>
                        View all marks
                    </Button>
                    <Button variant="contained" onClick={() => navigate(`/Admin/students/report-card/${studentID}`)} sx={{ borderRadius: 2, alignSelf: "flex-start" }}>
                        Report card
                    </Button>
                </>
            ) : (
                <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
                    <Typography color="text.secondary" gutterBottom>
                        No marks recorded yet.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Marks are entered by teachers and approved under Exams → Marks review.
                    </Typography>
                </Paper>
            )}
        </Stack>
    );

    return (
        <Box
            sx={{
                minHeight: "100%",
                pb: 4,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[50]} 22%)`
                        : alpha(t.palette.common.black, 0.15),
            }}
        >
            {loading ? (
                <Container maxWidth="md" sx={{ py: 4 }}>
                    <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3, mb: 2 }} />
                    <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2, mb: 2 }} />
                    <Skeleton variant="rounded" height={240} sx={{ borderRadius: 2 }} />
                </Container>
            ) : error ? (
                <Container maxWidth="sm" sx={{ py: 4 }}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography color="error">Could not load student.</Typography>
                    </Paper>
                </Container>
            ) : userDetails ? (
                <Fade in timeout={350}>
                    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <IconButton onClick={() => navigate(-1)} aria-label="Back" sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.08) }}>
                                <ArrowBack />
                            </IconButton>
                            <Typography variant="body2" color="text.secondary">
                                Students
                            </Typography>
                            <Typography variant="body2" color="text.disabled">
                                /
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                Profile
                            </Typography>
                        </Stack>

                        <TabContext value={tab}>
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    mb: 2,
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                    overflow: "hidden",
                                }}
                            >
                                <TabList
                                    onChange={(_, v) => setTab(v)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{
                                        px: 1,
                                        pt: 1,
                                        bgcolor: (t) => alpha(t.palette.background.paper, 0.9),
                                        "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 48 },
                                    }}
                                >
                                    <Tab label="Details" value="1" />
                                    <Tab label="Attendance" value="2" />
                                    <Tab label="Marks" value="3" />
                                    <Tab label="Special needs" value="4" />
                                </TabList>
                            </Paper>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 2, sm: 3 },
                                    borderRadius: 3,
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                    minHeight: 320,
                                }}
                            >
                                <TabPanel value="1" sx={{ p: 0 }}>
                                    <StudentDetailsSection />
                                </TabPanel>
                                <TabPanel value="2" sx={{ p: 0 }}>
                                    <StudentAttendanceSection />
                                </TabPanel>
                                <TabPanel value="3" sx={{ p: 0 }}>
                                    <StudentMarksSection />
                                </TabPanel>
                                <TabPanel value="4" sx={{ p: 0 }}>
                                    <SpecialNeedsSection
                                        studentId={studentID}
                                        student={userDetails}
                                        onUpdated={() => dispatch(getUserDetails(studentID, address))}
                                    />
                                </TabPanel>
                            </Paper>
                        </TabContext>
                    </Container>
                </Fade>
            ) : (
                <Container sx={{ py: 4 }}>
                    <Typography>{typeof response === "string" ? response : "Student not found."}</Typography>
                </Container>
            )}
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
            <ConfirmDeleteDialog
                open={deleteDialog}
                onClose={() => setDeleteDialog(false)}
                onConfirm={handleConfirmDeleteStudent}
                title={`Delete student: ${studentName}`}
                description="This will permanently delete this student and all their fee records, attendance, marks, and portal access."
                confirmWord={studentName}
            />
        </Box>
    );
};

export default ViewStudent;
